import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { toast } from "react-hot-toast";
import { auth } from "../lib/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber, User as FirebaseUser, ConfirmationResult } from "firebase/auth";
import { createUserProfile, findUserByPhone  } from "../services/auth.service";
import { getUserProfile } from "../services/user.service"; // Import new service
import { useNavigate } from "react-router-dom";

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier | null;
    grecaptcha?: any;
  }
}

type PhoneStepFields = {
  phone: string;
};

type ProfileStepFields = {
  name: string;
  address: string;
  email?: string;
};

const SignUp: React.FC = () => {
  const [step, setStep] = useState<"phone" | "otp" | "profile">("phone");
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  
  const [timer, setTimer] = useState(0);
  const navigate = useNavigate();

  const phoneForm = useForm<PhoneStepFields>();
  const otpForm = useForm<{ otp: string }>();
  const profileForm = useForm<ProfileStepFields>();

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const setupRecaptcha = () => {
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {
        console.warn("Could not clear recaptcha", e);
      }
      window.recaptchaVerifier = null;
    }

    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      { 
        size: "invisible",
        "expired-callback": () => {
             toast.error("Recaptcha expired. Please try again.");
        }
      }
    );
  };

  const handlePhoneSubmit = async (data: PhoneStepFields) => {
    try {
      setupRecaptcha();
      
      const appVerifier = window.recaptchaVerifier as RecaptchaVerifier;
      const formattedPhone = "+91" + data.phone.replace(/^(\+91)?/, "").replace(/\s/g, ""); 
      setPhoneNumber(formattedPhone);
      
      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(confirmation);
      setStep("otp");
      setTimer(45); 
      toast.success("OTP sent!");
    } catch (error: any) {
      console.error("SMS Error:", error);
      if (error.code === 'auth/invalid-phone-number') {
        toast.error("Invalid phone number format.");
      } else if (error.code === 'auth/too-many-requests') {
        toast.error("Too many attempts. Try again later.");
      } else {
        toast.error(error.message || "Failed to send OTP.");
      }
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0 || !phoneNumber) return;
    try {
      setupRecaptcha();
      const appVerifier = window.recaptchaVerifier as RecaptchaVerifier;
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(confirmation);
      setTimer(45); 
      toast.success("OTP resent successfully!");
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to resend OTP.");
    }
  };

const handleOtpSubmit = async (data: { otp: string }) => {
  if (!confirmationResult) return;
  try {
    // 1. Verify OTP
    const result = await confirmationResult.confirm(data.otp);
    const user = result.user;
    console.log(user);
    // 2. Check if profile exists by PHONE NUMBER (not UID)
    // This ensures we find users logging in again with the same phone
    const existingProfile = await findUserByPhone(phoneNumber);
	console.log(existingProfile);
    if (existingProfile) {
      // User exists -> Go Home (no duplicate created)
      toast.success("Welcome back!");
      navigate("/"); 
    } else {
      // New User -> Go to Profile Creation
      setFirebaseUser(user);
      setStep("profile");
      toast.success("Phone verified! Please complete your profile.");
    }
  } catch (error: any) {
    console.error("OTP Error:", error);
    toast.error("Incorrect OTP. Please check and try again.");
  }
};

  const handleProfileSubmit = async (data: ProfileStepFields) => {
    if (!firebaseUser) return;
    try {
      await createUserProfile(firebaseUser, {
        name: data.name,
        phone: phoneNumber,
        address: data.address,
        email: data.email,
      });
      toast.success("Profile created!");
      navigate("/"); 
    } catch (error: any) {
      toast.error(error.message || "Profile setup failed.");
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 pt-8 pb-16 sm:py-16">
      <Card className="p-8">
        <h1 className="text-2xl font-bold mb-6 text-center">Log In / Sign Up</h1>
        
        <div id="recaptcha-container"></div>

        {step === "phone" && (
          <form className="flex flex-col gap-4" onSubmit={phoneForm.handleSubmit(handlePhoneSubmit)}>
            <div className="form-group">
              <label className="form-label">Mobile Number</label>
              <div className="flex items-center border rounded-md p-0 focus-within:ring-2 focus-within:ring-orange-500">
                <span className="bg-gray-100 px-3 py-2 rounded-l-md text-gray-700 select-none font-medium border-r">
                  +91
                </span>
                <input
                  type="tel"
                  className="form-control border-none text-center flex-1 py-2 outline-none"
                  {...phoneForm.register("phone", {
                    required: "Phone is required",
                    minLength: { value: 10, message: "Enter valid 10-digit number" },
                    maxLength: { value: 10, message: "Enter valid 10-digit number" },
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Only numbers allowed",
                    },
                  })}
                  placeholder="9876543210"
                  maxLength={10}
                  inputMode="numeric"
                  autoComplete="tel"
                />
              </div>
              {phoneForm.formState.errors.phone && (
                <p className="text-red-500 text-sm mt-1">{phoneForm.formState.errors.phone.message}</p>
              )}
            </div>
            <Button type="submit" className="w-full min-h-[44px]" disabled={phoneForm.formState.isSubmitting}>
              {phoneForm.formState.isSubmitting ? "Sending OTP..." : "Send OTP"}
            </Button>
          </form>
        )}

        {step === "otp" && (
          <form className="flex flex-col gap-4" onSubmit={otpForm.handleSubmit(handleOtpSubmit)}>
            <div className="form-group">
              <label className="form-label">Verify OTP</label>
              <input
                type="text"
                className="form-control text-center tracking-[0.5em] font-mono text-xl"
                {...otpForm.register("otp", {
                  required: "OTP is required",
                  minLength: { value: 6, message: "Enter 6-digit code" },
                })}
                placeholder="------"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
              {otpForm.formState.errors.otp && (
                <p className="text-red-500 text-sm mt-1">{otpForm.formState.errors.otp.message}</p>
              )}
            </div>
            
            <div className="flex justify-between items-center text-sm">
               <button 
                 type="button" 
                 className="text-gray-500 hover:text-gray-700 underline"
                 onClick={() => setStep("phone")}
               >
                 Change Number
               </button>
               
               {timer > 0 ? (
                  <span className="text-gray-400 font-medium">
                     Resend in {timer}s
                  </span>
               ) : (
                  <button
                     type="button"
                     onClick={handleResendOtp}
                     className="font-medium text-orange-600 hover:text-orange-500 underline"
                  >
                     Resend OTP
                  </button>
               )}
            </div>

            <Button type="submit" className="w-full min-h-[44px]" disabled={otpForm.formState.isSubmitting}>
              {otpForm.formState.isSubmitting ? "Verifying..." : "Verify & Login"}
            </Button>
          </form>
        )}

        {step === "profile" && (
          <form className="flex flex-col gap-4" onSubmit={profileForm.handleSubmit(handleProfileSubmit)}>
            <div className="bg-green-50 text-green-700 p-3 rounded mb-2 text-sm text-center">
               ✅ Phone Verified! Setup your profile.
            </div>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                className="form-control"
                {...profileForm.register("name", { required: "Name is required" })}
                placeholder="e.g. Rahul Kumar"
              />
              {profileForm.formState.errors.name && (
                <p className="text-red-500 text-sm mt-1">{profileForm.formState.errors.name.message}</p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Delivery Address</label>
              <input
                type="text"
                className="form-control"
                {...profileForm.register("address", { required: "Address is required" })}
                placeholder="e.g. Flat 101, Residency Road..."
              />
              {profileForm.formState.errors.address && (
                <p className="text-red-500 text-sm mt-1">{profileForm.formState.errors.address.message}</p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Email (optional)</label>
              <input
                type="email"
                className="form-control"
                {...profileForm.register("email")}
                placeholder="For order receipts"
              />
            </div>
            <Button type="submit" disabled={profileForm.formState.isSubmitting}>
              {profileForm.formState.isSubmitting ? "Creating Account..." : "Complete Setup"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
};

export default SignUp;